import { SKY_OVERRIDES, turnMinuteOf } from "odyssey-shared";
import { say } from "../../../words.ts";
import type { LightingControl } from "./control.ts";

/**
 * The developer slider of the light layer (ADR-141's amendment of 2026-10-31, P2.25-T7), mounted only on a page opened with
 * `?lights=dev`: a range over the minute of the turn that holds the clock still, a select for the sky override, and a switch
 * back to the clock. It writes the page's lighting control, which the module reads each frame, and shows the minute the
 * clock holds while it follows it.
 */

/**
 * The slider's handle on the page.
 */
export interface LightSlider {
  /** Keeps the range at the clock's minute while the clock is followed. */
  update(): void;
  /** Removes the slider from the page. */
  destroy(): void;
}

const STEP = 0.25;
const TURN_MINUTES = 60;

/**
 * Mounts the slider at the page's lower left.
 *
 * @param control The control it writes.
 * @param doc The document to mount in.
 * @returns The slider's handle.
 */
export function mountLightSlider(control: LightingControl, doc: Document = document): LightSlider {
  const panel = doc.createElement("div");
  panel.setAttribute("data-light-slider", "");
  panel.style.cssText = "position:fixed;left:50%;top:8px;transform:translateX(-50%);z-index:50;display:flex;gap:8px;align-items:center;padding:6px 8px;background:rgba(10,14,24,0.85);color:#e8ecf4;font:12px sans-serif";
  const range = doc.createElement("input");
  range.type = "range";
  range.min = "0";
  range.max = String(TURN_MINUTES);
  range.step = String(STEP);
  range.value = "0";
  range.setAttribute("aria-label", say("light.slider.minute"));
  const sky = doc.createElement("select");
  sky.setAttribute("aria-label", say("light.slider.sky"));
  for (const name of SKY_OVERRIDES) {
    const option = doc.createElement("option");
    option.value = name;
    option.textContent = say(`light.slider.skies.${name}`);
    sky.appendChild(option);
  }
  const clock = doc.createElement("button");
  clock.type = "button";
  clock.textContent = say("light.slider.clock");
  range.addEventListener("input", () => {
    control.minute = Number(range.value);
  });
  sky.addEventListener("change", () => {
    control.sky = sky.value as (typeof SKY_OVERRIDES)[number];
  });
  clock.addEventListener("click", () => {
    control.minute = null;
    control.day = null;
  });
  panel.append(range, sky, clock);
  doc.body.appendChild(panel);
  return {
    update() {
      sky.value = control.sky;
      if (control.minute === null && control.serverTime !== null) {
        range.value = String(Math.round(turnMinuteOf(control.serverTime) / STEP) * STEP);
      }
    },
    destroy() {
      panel.remove();
    },
  };
}
